import { Component, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface ClassroomTreeNode {
    text: string;
    value: any;
    /** Parent label supplied by the classrooms dropdown API (course name). */
    parent?: string;
    children?: ClassroomTreeNode[];
}

/**
 * Dropdown tree-select for the classroom hierarchy (degree → course →
 * section). Standalone replacement for the Angular 13 workspace's
 * ngx-treeview based `app-single-classroom` — same selector and
 * value/valueChange API, so migrated templates work unchanged.
 * Leaf nodes are selectable; branches expand/collapse.
 */
@Component({
    selector: 'app-single-classroom',
    imports: [CommonModule, FormsModule],
    templateUrl: './single-classroom.component.html',
    styleUrl: './single-classroom.component.scss'
})
export class SingleClassroomComponent implements OnChanges {

    @Input() data: ClassroomTreeNode[] | null = null;
    @Input() value: number | null = null;
    @Output() valueChange = new EventEmitter<number>();

    open = false;
    filterText = '';
    expanded = new Set<ClassroomTreeNode>();
    buttonLabel = 'Select classroom';

    ngOnChanges(): void {
        this.updateLabel();
    }

    get filteredNodes(): ClassroomTreeNode[] {
        const q = this.filterText.trim().toLowerCase();
        if (!q) return this.data || [];
        return this.filterTree(this.data || [], q);
    }

    private filterTree(nodes: ClassroomTreeNode[], q: string): ClassroomTreeNode[] {
        const out: ClassroomTreeNode[] = [];
        for (const node of nodes) {
            const selfMatch = (node.text || '').toLowerCase().includes(q);
            const children = node.children?.length ? this.filterTree(node.children, q) : [];
            if (selfMatch && (!node.children || !node.children.length)) {
                out.push(node);
            } else if (children.length) {
                out.push({ ...node, children });
            } else if (selfMatch) {
                out.push(node);
            }
        }
        return out;
    }

    isLeaf(node: ClassroomTreeNode): boolean {
        return !node.children || node.children.length === 0;
    }

    isExpanded(node: ClassroomTreeNode): boolean {
        return !!this.filterText.trim() || this.expanded.has(node);
    }

    toggleNode(node: ClassroomTreeNode, event: Event): void {
        event.stopPropagation();
        if (this.isLeaf(node)) {
            this.selectLeaf(node);
            return;
        }
        if (this.expanded.has(node)) {
            this.expanded.delete(node);
        } else {
            this.expanded.add(node);
        }
    }

    selectLeaf(node: ClassroomTreeNode): void {
        const id = Number(node.value?.value ?? node.value);
        if (!Number.isNaN(id)) {
            this.value = id;
            this.valueChange.emit(id);
        }
        this.buttonLabel = SingleClassroomComponent.leafLabel(node);
        this.open = false;
    }

    /** Trigger label shows the class AND the section, e.g. "Grade 5 · A". */
    static leafLabel(node: ClassroomTreeNode): string {
        const section = node.text || 'Select classroom';
        return node.parent ? `${node.parent} · ${section}` : section;
    }

    toggleOpen(event: Event): void {
        event.stopPropagation();
        this.open = !this.open;
        if (this.open) this.filterText = '';
    }

    @HostListener('document:click')
    close(): void {
        this.open = false;
    }

    private updateLabel(): void {
        if (this.value == null || !this.data) {
            if (this.value == null) this.buttonLabel = 'Select classroom';
            return;
        }
        const found = this.findLeaf(this.data, Number(this.value));
        if (found) this.buttonLabel = SingleClassroomComponent.leafLabel(found);
    }

    private findLeaf(nodes: ClassroomTreeNode[], id: number): ClassroomTreeNode | null {
        for (const node of nodes) {
            if (this.isLeaf(node)) {
                const nodeId = Number(node.value?.value ?? node.value);
                if (nodeId === id) return node;
            } else if (node.children) {
                const hit = this.findLeaf(node.children, id);
                if (hit) return hit;
            }
        }
        return null;
    }
}
